import React, { useState } from 'react';
import { Button, message, Typography } from 'antd';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { DeleteOutlined, DragOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import showDeleteConfirm from '../../../../components/Confirmation-modal';
import { TRADEBOOK } from '../../../../utilities/functions';

import CreatePlanModal from './CreatePlanModal';
import PlanListItem from './PlanListItem';

import './styles.scss';

const { Title } = Typography;

export default function TradingPlanSelection({ plans, selectedPlan, onSelectPlan }) {
    const queryClient = useQueryClient();
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

    // Create new plan mutation
    const createPlanMutation = useMutation((planData) => TRADEBOOK.createTradingPlan(planData), {
        onSuccess: (newPlan) => {
            setIsCreateModalVisible(false);
            queryClient.invalidateQueries(['tradebook-plans']);
            onSelectPlan(newPlan._id);
            message.success('Trading plan created successfully');
        },
        onError: (error) => {
            message.error(error.response?.data?.error || 'Failed to create plan');
        }
    });

    // Delete plan mutation
    const deletePlanMutation = useMutation((planId) => TRADEBOOK.deleteTradingPlan(planId), {
        onSuccess: () => {
            queryClient.invalidateQueries(['tradebook-plans']);
            queryClient.invalidateQueries(['tradebook-stats']);
            onSelectPlan(null);
            message.success('Trading plan deleted');
        },
        onError: () => {
            message.error('Failed to delete plan');
        }
    });

    // Reorder plans mutation
    const reorderPlansMutation = useMutation((planIds) => TRADEBOOK.reorderTradingPlans(planIds), {
        onSuccess: () => {
            queryClient.invalidateQueries(['tradebook-plans']);
        }
    });

    const handleCreatePlan = () => {
        setIsCreateModalVisible(true);
    };

    const handleModalCreate = (strategyName) => {
        createPlanMutation.mutate({
            strategyName,
            time: [],
            indicators: [],
            confirmations: [],
            entrySignal: [],
            stopLoss: [],
            targets: [],
            specificRules: [],
            riskPerTrade: [],
            profitTargetPerTrade: [],
            maxLossPerTrade: [],
            stopLossMechanism: [],
            partialPlan: [],
            addingToPosition: [],
            howToManageStop: []
        });
    };

    const handleModalCancel = () => {
        setIsCreateModalVisible(false);
    };

    const handleDeletePlan = (planId, strategyName) => {
        showDeleteConfirm(
            (confirmed) => {
                if (confirmed) {
                    deletePlanMutation.mutate(planId);
                }
            },
            strategyName,
            'Delete this trading plan? All trades assigned to this strategy will be unassigned.'
        );
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(plans);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const planIds = items.map((plan) => plan._id);
        reorderPlansMutation.mutate(planIds);
    };

    return (
        <div className="trading-plan-selection">
            <div className="selection-header">
                <Title level={4}>Trading Plans</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreatePlan}
                    loading={createPlanMutation.isLoading}
                >
                    New Plan
                </Button>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="plans">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="plans-list"
                        >
                            {plans?.map((plan, index) => (
                                <Draggable key={plan._id} draggableId={plan._id} index={index}>
                                    {(dragProvided, snapshot) => (
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={`plan-item ${
                                                selectedPlan === plan._id ? 'selected' : ''
                                            } ${snapshot.isDragging ? 'dragging' : ''}`}
                                        >
                                            <div
                                                {...dragProvided.dragHandleProps}
                                                className="drag-handle"
                                            >
                                                <DragOutlined />
                                            </div>

                                            <div
                                                className="plan-content"
                                                onClick={() => onSelectPlan(plan._id)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        onSelectPlan(plan._id);
                                                    }
                                                }}
                                            >
                                                <PlanListItem plan={plan} />
                                            </div>

                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePlan(plan._id, plan.strategyName);
                                                }}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <CreatePlanModal
                visible={isCreateModalVisible}
                onCancel={handleModalCancel}
                onCreate={handleModalCreate}
                isLoading={createPlanMutation.isLoading}
            />
        </div>
    );
}

TradingPlanSelection.propTypes = {
    plans: PropTypes.array,
    selectedPlan: PropTypes.string,
    onSelectPlan: PropTypes.func
};
